import {
  getAccessTokenByAuthCode,
  LOGIN_TOKEN_LOCAL_STORAGE_KEY,
  saveToStorage,
} from "@bn/api";
import { USE_INTERNAL_AUTH } from "./authEnvironments";

export const AUTH_CALLBACK_PATH = "/auth/callback";

const PENDING_LOGIN_STORAGE_KEY = "PORTAL_CODE_LOGIN";
const RETRIED_LOGIN_STORAGE_KEY = "PORTAL_CODE_LOGIN_RETRIED";

export class PortalLoginNotStartedError extends Error {
  constructor() {
    super("Portal login callback with unexpected state");
    this.name = "PortalLoginNotStartedError";
  }
}

interface PendingLogin {
  state: string;
  returnTo?: string;
}

const trimTrailingSlash = (url: string) => url.replace(/\/+$/, "");

const getPortalAuthUrl = (): string | undefined => {
  const url = import.meta.env.VITE_APP_PORTAL_AUTH_URL;
  return url ? trimTrailingSlash(url) : undefined;
};

const getAppUrl = () =>
  trimTrailingSlash(import.meta.env.VITE_APP_APP_URL || window.location.origin);

const isAppOrigin = (url: string) => {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
};

const createState = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");

const readPendingLogin = (): PendingLogin | undefined => {
  const stored = sessionStorage.getItem(PENDING_LOGIN_STORAGE_KEY);
  if (!stored) return undefined;

  try {
    return JSON.parse(stored) as PendingLogin;
  } catch {
    return undefined;
  }
};

/**
 * Whether login runs through the mandator portal with an auth code: the
 * portal redirects back to AUTH_CALLBACK_PATH with `?code=`, which the app
 * exchanges at VITE_APP_PORTAL_AUTH_URL. Needed where the app runs on its own
 * subdomain, so the portal cannot write the token into the app's storage.
 */
export const isPortalCodeLogin = () =>
  !USE_INTERNAL_AUTH && Boolean(getPortalAuthUrl());

export const getPortalRefreshUrl = () => {
  const authUrl = getPortalAuthUrl();
  return isPortalCodeLogin() && authUrl ? `${authUrl}/refresh` : undefined;
};

export const startPortalCodeLogin = (
  loginUrl: string,
  returnTo?: string
): string => {
  const state = createState();
  const pending: PendingLogin = {
    state,
    returnTo: returnTo ?? readPendingLogin()?.returnTo,
  };
  sessionStorage.setItem(PENDING_LOGIN_STORAGE_KEY, JSON.stringify(pending));

  const url = new URL(loginUrl, window.location.origin);
  url.searchParams.set("redirect_uri", `${getAppUrl()}${AUTH_CALLBACK_PATH}`);
  url.searchParams.set("state", state);
  return url.toString();
};

/**
 * Exchanges the portal's auth code for tokens and returns the URL to continue
 * at. Rejects callbacks whose `state` does not match the login this tab
 * started, so a code planted from elsewhere cannot log the user into a
 * foreign account.
 */
export const completePortalCodeLogin = async ({
  code,
  state,
}: {
  code: string | null;
  state: string | null;
}): Promise<string> => {
  const pending = readPendingLogin();
  const authUrl = getPortalAuthUrl();

  if (!authUrl) {
    throw new Error("Portal auth URL is not configured");
  }
  if (!code) {
    throw new Error("Portal login callback without code");
  }
  if (!pending || !state || pending.state !== state) {
    throw new PortalLoginNotStartedError();
  }

  sessionStorage.removeItem(PENDING_LOGIN_STORAGE_KEY);
  const token = await getAccessTokenByAuthCode({
    tokenUrl: `${authUrl}/token`,
    code,
  });
  saveToStorage(token, true);
  // The app reads the per-tab token first, so an older one would win.
  sessionStorage.removeItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY);
  sessionStorage.removeItem(RETRIED_LOGIN_STORAGE_KEY);

  return pending.returnTo && isAppOrigin(pending.returnTo)
    ? pending.returnTo
    : getAppUrl();
};

/**
 * A callback this tab did not start (new tab, reopened link, login started by
 * the portal) gets one fresh login instead of an error. The flag stops a loop
 * when the portal keeps answering without our state.
 */
export const takePortalLoginRetry = (): boolean => {
  if (sessionStorage.getItem(RETRIED_LOGIN_STORAGE_KEY)) return false;
  sessionStorage.setItem(RETRIED_LOGIN_STORAGE_KEY, "true");
  return true;
};
